import { Text } from '@/components/ui/text';
import { useAppearance } from '@/lib/design/appearance';
import { getDesignTokens } from '@/lib/design/theme';
import * as React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

export function PublicWebRedirect({ label, targetURL }: { label: string; targetURL: string }) {
  const resolvedMode = useAppearance((state) => state.resolvedMode);
  const tokens = React.useMemo(() => getDesignTokens(resolvedMode), [resolvedMode]);
  const styles = React.useMemo(() => createStyles(tokens), [tokens]);

  React.useEffect(() => {
    globalThis.location?.replace(targetURL);
  }, [targetURL]);

  return (
    <View style={styles.root}>
      <View style={styles.panel}>
        <Text style={styles.wordmark}>
          radious<Text style={styles.dot}>.</Text>
        </Text>
        <Text style={styles.title}>Opening {label}</Text>
        <Text style={styles.body}>
          If the page does not move automatically, use the button below.
        </Text>
        <Pressable
          accessibilityRole="link"
          style={({ pressed }) => [styles.button, pressed ? styles.pressed : null]}
          onPress={() => globalThis.location?.assign(targetURL)}>
          <Text style={styles.buttonText}>Open {label}</Text>
        </Pressable>
      </View>
    </View>
  );
}

function createStyles(tokens: ReturnType<typeof getDesignTokens>) {
  return StyleSheet.create({
    root: {
      alignItems: 'center',
      backgroundColor: tokens.colors.canvas,
      flex: 1,
      justifyContent: 'center',
      minHeight: '100%',
      padding: 24,
    },
    panel: {
      alignItems: 'flex-start',
      backgroundColor: tokens.colors.surfaceOverlay,
      borderColor: tokens.colors.borderStrong,
      borderRadius: 28,
      borderWidth: 1,
      gap: 14,
      maxWidth: 440,
      padding: 24,
      width: '100%',
      ...tokens.shadows.card,
    },
    wordmark: {
      color: tokens.colors.text,
      fontFamily: tokens.typography.fonts.displayExtraBold,
      fontSize: 34,
      letterSpacing: -2,
      lineHeight: 39,
    },
    dot: {
      color: tokens.colors.accentStrong,
    },
    title: {
      color: tokens.colors.text,
      fontFamily: tokens.typography.fonts.displayBold,
      fontSize: 28,
      letterSpacing: -1,
      lineHeight: 33,
    },
    body: {
      color: tokens.colors.textSoft,
      fontFamily: tokens.typography.fonts.bodyMedium,
      fontSize: 16,
      lineHeight: 24,
    },
    button: {
      backgroundColor: tokens.colors.accentStrong,
      borderColor: tokens.colors.accentEdge,
      borderRadius: tokens.radii.pill,
      borderWidth: 1,
      paddingHorizontal: 18,
      paddingVertical: 12,
    },
    buttonText: {
      color: tokens.colors.textInverse,
      fontFamily: tokens.typography.fonts.bodyBold,
      fontSize: 15,
    },
    pressed: {
      opacity: 0.72,
    },
  });
}
